import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Loader2, Sunrise, ArrowLeft, Quote, Facebook, MessageCircle, Link2, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";

export default function DevotionArchive() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/devotions/archive").then((r) => {
      setItems(r.data);
      const id = params.get("d");
      if (id) { const m = r.data.find((x) => x.id === id); if (m) setSelected(m); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const open = (d) => { setSelected(d); setParams({ d: d.id }); window.scrollTo(0, 0); };
  const close = () => { setSelected(null); setParams({}); };

  const shareUrl = (d) => `${window.location.origin}/devotions?d=${d.id}`;
  const shareText = (d) => `${d.title} — ${d.verse_reference} | Devotion of the Day from In His Image Radio`;
  const shareFacebook = (d) => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(d))}`, "_blank", "noopener,width=640,height=640");
  const shareWhatsApp = (d) => window.open(`https://wa.me/?text=${encodeURIComponent(shareText(d) + " " + shareUrl(d))}`, "_blank", "noopener");
  const copyLink = async (d) => { try { await navigator.clipboard.writeText(shareUrl(d)); toast.success("Link copied — share it anywhere"); } catch { toast.error("Could not copy link"); } };
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const nativeShare = async (d) => {
    try { await navigator.share({ title: d.title, text: shareText(d), url: shareUrl(d) }); }
    catch (e) { if (e?.name !== "AbortError") copyLink(d); }
  };

  const ShareRow = ({ d, testid }) => (
    <div className="flex items-center gap-2" data-testid={testid}>
      {canNativeShare && (
        <button onClick={() => nativeShare(d)} data-testid="share-native" title="Share" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors"><Share2 className="w-4 h-4" /> Share</button>
      )}
      <span className="text-xs text-zinc-500 mr-1">Share:</span>
      <button onClick={() => shareFacebook(d)} data-testid="share-facebook" title="Share on Facebook" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#1877F2] text-zinc-300 hover:text-white flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></button>
      <button onClick={() => shareWhatsApp(d)} data-testid="share-whatsapp" title="Share on WhatsApp" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#25D366] text-zinc-300 hover:text-white flex items-center justify-center transition-colors"><MessageCircle className="w-4 h-4" /></button>
      <button onClick={() => copyLink(d)} data-testid="share-copy" title="Copy link" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#E6B34A] text-zinc-300 hover:text-[#0A1030] flex items-center justify-center transition-colors"><Link2 className="w-4 h-4" /></button>
    </div>
  );

  if (selected) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="devotion-reader">
        <button onClick={close} data-testid="devotion-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to devotions
        </button>
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#E6B34A] mb-2">{fmtDate(selected.date)}</p>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mb-6">{selected.title}</h1>
        <div className="bg-[#141C42] border-l-4 border-[#E6B34A] rounded-xl p-5 mb-6">
          <Quote className="w-5 h-5 text-[#E6B34A] mb-2" />
          <p className="text-lg italic text-[#fdf0d5] leading-relaxed">{selected.verse_text}</p>
          <p className="text-[#E6B34A] font-bold text-sm mt-3">— {selected.verse_reference}</p>
        </div>
        <p className="text-[15px] leading-8 text-zinc-200 whitespace-pre-line mb-8">{selected.reflection}</p>
        <div className="bg-[#101735] rounded-xl p-5 mb-8">
          <p className="text-[11px] tracking-[0.15em] uppercase text-[#E6B34A] mb-2">A Prayer</p>
          <p className="text-[15px] leading-7 italic text-zinc-300">{selected.prayer}</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div className="text-sm text-zinc-400">Blessings,<br /><span className="font-display text-[#E6B34A] text-lg">David Porter</span> · In His Image Radio</div>
          <ShareRow d={selected} testid="reader-share" />
        </div>
      </div>
    );
  }

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="devotion-archive">
      <div className="flex items-center gap-3 mb-2">
        <Sunrise className="w-7 h-7 text-[#E6B34A]" />
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Devotion of the Day</h1>
      </div>
      <p className="text-zinc-400 mb-8 max-w-xl">A daily word of encouragement from In His Image Radio. Catch up on past devotions any time.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 py-16"><Loader2 className="w-5 h-5 animate-spin" /> Loading devotions…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No devotions yet — check back soon for your first daily word.</p>
        </div>
      ) : (
        <>
          {featured && (
            <div className="relative overflow-hidden rounded-3xl border border-[#E6B34A]/30 bg-gradient-to-br from-[#1A2452] to-[#0C1338] p-6 sm:p-8 mb-8" data-testid="devotion-featured">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E6B34A] text-[#0A1030] text-[11px] font-bold uppercase tracking-wide mb-4"><Sparkles className="w-3.5 h-3.5" /> Today's Devotion</span>
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#E6B34A] mb-1">{fmtDate(featured.date)}</p>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-3">{featured.title}</h2>
              <p className="text-base italic text-[#fdf0d5] leading-relaxed mb-1">“{featured.verse_text}”</p>
              <p className="text-[#E6B34A] font-bold text-sm mb-5">— {featured.verse_reference}</p>
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={() => open(featured)} data-testid="featured-read" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Read today's devotion</button>
                <ShareRow d={featured} testid="featured-share" />
              </div>
            </div>
          )}

          {rest.length > 0 && (
            <>
              <h3 className="text-sm uppercase tracking-[0.15em] text-zinc-500 mb-4">Earlier devotions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {rest.map((d) => (
                  <button key={d.id} onClick={() => open(d)} data-testid={`devotion-card-${d.id}`}
                    className="text-left bg-[#141C42] hover:bg-[#1A2452] border border-white/5 hover:border-[#E6B34A]/40 rounded-2xl p-5 transition-colors group">
                    <p className="text-[11px] tracking-[0.15em] uppercase text-[#E6B34A] mb-2">{fmtDate(d.date)}</p>
                    <h2 className="font-display font-bold text-xl mb-3 group-hover:text-[#E6B34A] transition-colors">{d.title}</h2>
                    <p className="text-sm italic text-zinc-300 line-clamp-2">“{d.verse_text}”</p>
                    <p className="text-[#E6B34A] text-xs font-semibold mt-2">— {d.verse_reference}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
